<script setup>
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, object, string } from 'yup';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
});

const types = [
    { value: 'shipping', label: 'Livraison' },
    { value: 'payment', label: 'Paiement' },
    { value: 'review', label: 'Avis' },
    { value: 'search', label: 'Recherche' },
    { value: 'checkout', label: 'Checkout' },
];

const { route } = useRoute();

const form = reactive({
    eCommerceModules:
        tenant && tenant.eCommerceModules && tenant.eCommerceModules.length > 0
            ? tenant.eCommerceModules.map((eCommerceModule) => ({
                  type: types.find(
                      (type) => type.value === eCommerceModule.type,
                  ),
                  name: eCommerceModule.name,
                  version: eCommerceModule.version,
              }))
            : [
                  {
                      type: types[0],
                      name: '',
                      version: '',
                  },
              ],
});

const validationSchema = object({
    eCommerceModules: array().of(
        object({
            type: object().required('Ce champ est obligatoire.'),
            name: string().required('Ce champ est obligatoire.'),
            version: string().nullable(),
        }),
    ),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.dev.postECommerceModules', {
                tenant: tenant.id,
            }),
            {
                eCommerceModules: form.eCommerceModules.map(
                    (eCommerceModule) => ({
                        type: eCommerceModule.type.value,
                        name: eCommerceModule.name,
                        version: eCommerceModule.version,
                    }),
                ),
            },
        )
        .then(() => {
            toastSaved();

            goBack();
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid
                    v-for="(eCommerceModule, index) in form.eCommerceModules"
                    :key="index"
                >
                    <ListboxField
                        v-model="eCommerceModule.type"
                        :field-name="'eCommerceModules[' + index + '].type'"
                        :field-id="'eCommerceModules[' + index + '].type'"
                        label="Type"
                        :options="types"
                    >
                        <template #buttonContent>
                            {{ eCommerceModule.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="eCommerceModule.name"
                        :label="'Nom ' + (index + 1)"
                        :field-name="'eCommerceModules[' + index + '].name'"
                        :field-id="'eCommerceModules[' + index + '].name'"
                        required
                    />

                    <InputField
                        v-model="eCommerceModule.version"
                        :label="'Version ' + (index + 1)"
                        :field-name="'eCommerceModules[' + index + '].version'"
                        :field-id="'eCommerceModules[' + index + '].version'"
                    />

                    <div class="mb-4 flex items-end justify-start">
                        <ActionButton
                            :callback="
                                () => form.eCommerceModules.splice(index, 1)
                            "
                        >
                            Supprimer
                        </ActionButton>
                    </div>
                </FormGrid>

                <ActionButton
                    :callback="
                        () =>
                            form.eCommerceModules.push({
                                type: types[0],
                                name: '',
                                version: '',
                            })
                    "
                >
                    Ajouter
                </ActionButton>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
